<template>
    <div>
        <plan-selector v-if="selectedPackage === null" @package-selected="onPackageSelected"></plan-selector>
        <div v-else>
            <h3 class="text-2xl font-thin mb-3">Selected Plan</h3>
            <div class="flex flex-wrap items-center justify-between rounded-lg bg-white shadow-lg py-10 px-8 text-gray-800 mb-8">
                <div>
                    <h2 class="font-bold uppercase text-xl text-gray-900 leading-none mb-2">
                        {{ selectedPackage.plan.name.toUpperCase() }}
                    </h2>
                    <h3 class="text-gray-500 leading-none">Job will be visible for {{ selectedPackage.plan.no_of_days }} days</h3>
                </div>
                <div class="flex flex-wrap items-center space-x-0 sm:space-x-6 space-y-6 sm:space-y-0">
                    <h3 class="text-gray-500 leading-none">
                        {{ selectedPackage.posted_projects }} out of {{ selectedPackage.total_projects }} jobs used from this
                        package
                    </h3>
                    <button class="btn btn-line-primary" @click="selectedPackage = null">
                        Change Plan
                    </button>
                </div>
            </div>

            <h3 class="text-2xl font-thin mb-3">Job Details</h3>
            <well>
                <form v-if="form" @submit.prevent="submitForm" @input="onFormChange">
                    <my-input
                        v-model="form.title"
                        name="title"
                        :error="form.errors.get('title')"
                        :autofucos="true"
                        class="mb-8 max-w-lg"
                        label="Job Title"
                        placeholder="Your job's title"
                    />

                    <my-multiselect
                        v-model="form.categories"
                        :options="allCategories"
                        name="categories"
                        label-by="name"
                        :error="form.errors.get('categories')"
                        label="Job Categories"
                        :reduce="category => category.id"
                        class="mb-8 max-w-lg"
                        :selection-limit="5"
                        placeholder="Choose up to maximum 5 categories..."
                    >
                        <template slot="option" slot-scope="option">
                            <span v-if="option.parent_id === null">{{ option.name }}</span>
                            <span v-if="option.parent_id">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{{ option.name }}</span>
                        </template>
                    </my-multiselect>

                    <rich-editor
                        v-model="form.description"
                        label="Job Description"
                        placeholder="Describe what you need here..."
                        class="max-w-3xl mb-8"
                    />

                    <multiple-attachments
                        ref="attachments"
                        label="Attachments (Public files)"
                        help-text="(Upload maximum 5 files with extensions including png, jpg, pdf, xls, and doc format)"
                        class="max-w-3xl mb-8"
                    />

                    <my-multiselect
                        v-model="form.skills"
                        :options="allSkills"
                        name="skills"
                        label-by="name.en"
                        :error="form.errors.get('skills')"
                        label="What skills do you require?"
                        class="mb-8 max-w-lg"
                        :selection-limit="5"
                        :reduce="skill => skill.name.en"
                        placeholder="Choose maximum 5 skills..."
                    />

                    <my-input
                        v-model="form.budget"
                        type="number"
                        name="budget"
                        :error="form.errors.get('budget')"
                        label="Your Job Budget (in US Dollars)"
                        class="max-w-lg"
                        placeholder="e.g. 20"
                    />
                    <div class="mb-8 text-gray-500 text-sm">
                        Optional
                    </div>

                    <my-select
                        v-model="form.country"
                        name="country"
                        :error="form.errors.get('country')"
                        label="Country"
                        :options="countries"
                        class="mb-8 max-w-lg"
                        placeholder="Choose a country"
                    />

                    <my-select
                        v-model="form.job_type"
                        name="job_type"
                        :error="form.errors.get('job_type')"
                        label="Job Type"
                        :options="job_types"
                        class="mb-8 max-w-lg"
                        placeholder="Choose a Job Type"
                    />

                    <hr class="mb-4" />

                    <my-button :loading="submitting" class="btn btn-primary rounded-full" type="submit">
                        Save as Draft
                    </my-button>
                </form>
            </well>
        </div>
    </div>
</template>

<script>
import Form from '../../forms/form';
import { delay } from '../../utils';

export default {
    data() {
        return {
            form: new Form(
                {
                    categories: [],
                    title: null,
                    description: null,
                    skills: [],
                    budget: null,
                    country: null,
                    job_type: null,
                    attachments: [],
                    subscription_id: null
                },
                { hasFiles: true, withFormData: true }
            ),
            submitting: false,
            showPlans: true,
            selectedPackage: null,
            countries: {},
            allSkills: [],
            job_types: { 1: 'Freelancing', 2: 'Regular', 3: 'External' },
            allCategories: []
        };
    },

    watch: {
        selectedPackage: function(newPackage) {
            this.form.subscription_id = newPackage ? newPackage.id : null;
        }
    },

    mounted() {
        this.fetchCountries();
        this.fetchAllSkills();
        this.fetchAllCategories();
    },

    methods: {
        submitForm() {
            NProgress.inc();
            this.submitting = true;
            this.form.attachments = this.$refs.attachments.files.map(file => file.file);

            this.form
                .post(`/api/projects`)
                .then(async response => {
                    flash.success({ message: 'Job created successfully. Redirecting you to the job page...' });
                    await delay(1000);
                    window.location.href = response.data.data.url;
                })
                .catch(() => {
                    if (this.form.errors.any()) {
                        flash.error({ message: 'It seems like you have submitted invalid data.' });
                        return;
                    }

                    flash.error();
                })
                .finally(() => {
                    NProgress.done();
                    this.submitting = false;
                });
        },

        fetchCountries() {
            axios.get('/api/countries').then(response => {
                this.countries = _.keyBy(response.data.data, value => value);
            });
        },

        fetchAllSkills() {
            axios.get(`/api/tags`).then(response => {
                this.allSkills = response.data.data;
            });
        },

        fetchAllCategories() {
            axios.get(`/api/categories?nested=1`).then(response => {
                this.allCategories = this.flattenCategories(response.data.data);
            });
        },

        flattenCategories(categories) {
            let flatten = [];

            categories.forEach(category => {
                flatten.push(category);

                category.children.forEach(childCategory => {
                    flatten.push(childCategory);
                });
            });

            return flatten;
        },

        onFormChange(event) {
            this.form.errors.clear(event.target.name);
        },

        onPackageSelected(selectedPackage) {
            this.selectedPackage = selectedPackage;
            this.form.subscription_id = selectedPackage.id;
        }
    }
};
</script>
